<template>
  <div class="content">
    <div class="container-fluid pt-3">
      <nav aria-label="breadcrumb">
        <ol class="breadcrumb" style="margin-bottom: 0.35rem !important;">
          <router-link :to="{ name: 'dashboard' }" class="breadcrumb-item">หน้าหลัก</router-link>
          <li class="breadcrumb-item active" aria-current="page">สรุปภาพรวม (Dashboard Summary)</li>
        </ol>
      </nav>

      <div class="row mt-3">
        <div class="col-md-12">
          <div class="card mb-3">
            <div class="card-body pt-3 pb-2">
              <div class="row">
                <div class="col-md-3 col-sm-12 mb-2">
                  <label class="mb-1">เลือกปี</label>
                  <select v-model="selectedYear" class="form-control">
                    <option value="0">ทั้งหมด</option>
                    <option v-for="year in availableYears" :key="year" :value="year">
                      {{ year }}
                    </option>
                  </select>
                </div>
                <div class="col-md-3 col-sm-12 mb-2">
                  <label class="mb-1">เลือกจังหวัด</label>
                  <select v-model="selectedProvince" class="form-control">
                    <option value="0">ทั้งหมด</option>
                    <option v-for="(province, index) in provinces" :key="index" :value="province.code">
                      {{ province.name_th }}
                    </option>
                  </select>
                </div>
                <div class="col-md-3 col-sm-12 mb-2">
                  <label class="mb-1">เลือกโรงพยาบาล</label>
                  <select v-model="selectedHospital" class="form-control" :disabled="selectedProvince === '0'">
                    <option value="0">ทั้งหมด</option>
                    <option v-for="(hospital, index) in filteredHospitals" :key="index" :value="hospital.hoscode">
                      {{ hospital.hosname }}
                    </option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          <div class="card">
            <div class="card-header d-flex align-items-center justify-content-between my-card-header pt-3">
              <div>
                <i class="fas fa-chart-bar mr-2"></i>
                ภาพรวมจำนวน AN รายเดือน (แยกตามระดับความเสี่ยง)
              </div>
            </div>
            <div class="card-body pt-0">
              <div v-if="isLoading" class="text-center py-4">
                <img src="/images/Spinner-1s-200px2.gif" width="40" alt="loading">
              </div>
              <div v-else class="chart-wrapper">
                <div class="chart-container">
                  <canvas ref="summaryChart"></canvas>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="row mt-3 mb-3">
        <div class="col-md-12">
          <div class="card pt-3">
            <div class="card-header my-card-header">
              <i class="fas fa-table mr-2"></i>
              ตารางสรุปรายเดือน
            </div>
            <div class="card-body table-responsive p-0">
              <table class="table table-striped table-hover mb-0">
                <thead class="thead-light">
                  <tr>
                    <th width="15%" class="text-center">เดือน</th>
                    <th v-for="color in availableRiskColors" :key="color" class="text-center" width="20%">
                      <span :class="['risk-badge']" :style="{ backgroundColor: colorStyles[color] || '#ced8e2' }">
                        {{ colorLabels[color] || color.toUpperCase() }}
                      </span>
                    </th>
                    <th width="15%" class="text-center">รวม</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="month in monthOrder" :key="month">
                    <td class="text-center font-weight-bold">{{ thaiMonthShort(month) }}</td>
                    <td v-for="color in availableRiskColors" :key="month + '-' + color" class="text-center">
                      {{ monthlyAggregates[month]?.[color] || 0 }}
                    </td>
                    <td class="text-center font-weight-bold">{{ monthTotals[month] || 0 }}</td>
                  </tr>
                </tbody>
                <tfoot class="table-footer">
                  <tr>
                    <td class="text-center font-weight-bold">รวมทั้งหมด</td>
                    <td v-for="color in availableRiskColors" :key="'total-' + color" class="text-center font-weight-bold">
                      {{ calculateTotalByColor(color) }}
                    </td>
                    <td class="text-center font-weight-bold">{{ calculateGrandTotal() }}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>
      </div>

    </div>
  </div>
  
</template>

<script>
import axios from 'axios'
import Chart from 'chart.js/auto'

export default {
  name: 'DashboardSummary',
  data() {
    return {
      isLoading: true,
      rawSummary: [],
      chartInstance: null,
      availableRiskColors: [],
      monthlyAggregates: {},
      monthTotals: {},
      provinces: [],
      hospitals: [],
      selectedYear: new Date().getFullYear().toString(),
      selectedProvince: '0',
      selectedHospital: '0',
      colorStyles: {
        green: '#04c786',
        yellow: '#FB9847',
        red: '#f6124e',
        orange: '#ff8c00',
        blue: '#58a7ff',
      },
      colorLabels: {
        green: 'เสี่ยงต่ำ',
        yellow: 'เสี่ยงปานกลาง',
        red: 'เสี่ยงสูง',
        orange: 'เสี่ยงสูงมาก',
        blue: 'เสี่ยงสูงสุด',
      },
    }
  },
  computed: {
    monthOrder() {
      // Return months (1..12) that actually appear in data, sorted ascending
      const months = Object.keys(this.monthlyAggregates).map(m => Number(m))
      months.sort((a, b) => a - b)
      return months
    },
    filteredHospitals() {
      if (this.selectedProvince === '0') return []
      return this.hospitals.filter(h => h.provinceCode === this.selectedProvince)
    },
    availableYears() {
      const years = [...new Set(this.rawSummary.map(item => item.years))].filter(Boolean)
      return years.sort((a, b) => b - a) // Sort descending (newest first)
    }
  },
  async created() {
    await Promise.all([
      this.fetchMeta(),
      this.fetchSummary(),
    ])
  },
  mounted() {
    this.renderChart()
  },
  watch: {
    monthlyAggregates: {
      deep: true,
      handler() {
        this.renderChart()
      }
    },
    selectedYear() {
      this.applyFiltersAndAggregate()
    },
    selectedProvince() {
      // reset hospital when province changes
      this.selectedHospital = '0'
      this.applyFiltersAndAggregate()
    },
    selectedHospital() {
      this.applyFiltersAndAggregate()
    }
  },
  methods: {
    calculateTotalByColor(color) {
      let total = 0
      for (const month of this.monthOrder) {
        total += this.monthlyAggregates[month]?.[color] || 0
      }
      return total
    },
    calculateGrandTotal() {
      let total = 0
      for (const month of this.monthOrder) {
        total += this.monthTotals[month] || 0
      }
      return total
    },
    async fetchMeta() {
      try {
        const urlProvince = process.env.VUE_APP_API_URL + '/dashboard/provinces/'
        const urlHospitals = process.env.VUE_APP_API_URL + '/dashboard/hospitals/'
        const [provRes, hosRes] = await Promise.all([
          axios.get(urlProvince),
          axios.get(urlHospitals),
        ])
        this.provinces = Array.isArray(provRes.data) ? provRes.data : []
        this.hospitals = Array.isArray(hosRes.data) ? hosRes.data : []
      } catch (e) {
        console.log(e)
        this.provinces = []
        this.hospitals = []
      }
    },
    async fetchSummary() {
      try {
        const token = localStorage.getItem('token')
        const url = process.env.VUE_APP_API_URL + '/dashboard/summary/'
        const config = {
          method: 'post',
          url,
          headers: { 'Content-Type': 'application/json' },
          data: JSON.stringify({ token })
        }
        const response = await axios.request(config)
        this.rawSummary = Array.isArray(response.data) ? response.data : []
        this.applyFiltersAndAggregate()
      } catch (error) {
        // Fallback to empty data on error
        console.log(error)
        this.rawSummary = []
        this.applyFiltersAndAggregate()
      } finally {
        this.isLoading = false
        // Ensure canvas is mounted before rendering chart
        this.$nextTick(() => {
          this.renderChart()
        })
      }
    },
    applyFiltersAndAggregate() {
      // Filter rawSummary by selected year/province/hospital (by name)
      let filtered = [...this.rawSummary]

      if (this.selectedYear !== '0') {
        filtered = filtered.filter(row => Number(row.years) === Number(this.selectedYear))
      }

      if (this.selectedProvince !== '0') {
        const provinceObj = this.provinces.find(p => p.code === this.selectedProvince)
        const provinceName = provinceObj ? provinceObj.name_th : null
        if (provinceName) {
          filtered = filtered.filter(row => (row.provname || '').trim() === provinceName)
        }
      }

      if (this.selectedHospital !== '0') {
        const hosObj = this.hospitals.find(h => h.hoscode === this.selectedHospital)
        const hosName = hosObj ? hosObj.hosname : null
        if (hosName) {
          filtered = filtered.filter(row => (row.hosname || '').trim() === hosName)
        }
      }

      this.buildAggregations(filtered)
    },
    buildAggregations(dataset = null) {
      const aggregates = {}
      const totals = {}
      const riskSet = new Set()

      const rows = Array.isArray(dataset) ? dataset : this.rawSummary

      for (const row of rows) {
        const month = Number(row.months)
        const color = (row.risk_color || '').toLowerCase()
        const count = Number(row.ans) || 0
        if (!aggregates[month]) aggregates[month] = {}
        aggregates[month][color] = (aggregates[month][color] || 0) + count
        totals[month] = (totals[month] || 0) + count
        if (color) riskSet.add(color)
      }

      // Ensure deterministic order of colors
      const preferredOrder = ['green', 'yellow', 'orange', 'red', 'blue']
      const presentColors = Array.from(riskSet)
      presentColors.sort((a, b) => preferredOrder.indexOf(a) - preferredOrder.indexOf(b))

      this.availableRiskColors = presentColors
      this.monthlyAggregates = aggregates
      this.monthTotals = totals
    },
    thaiMonthShort(month) {
      const m = [
        '', 'ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.',
        'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.'
      ]
      return m[Number(month)] || '-'
    },
    renderChart() {
      if (!this.$refs.summaryChart) return

      // Destroy existing chart to avoid duplicates
      if (this.chartInstance) {
        this.chartInstance.destroy()
      }

      const ctx = this.$refs.summaryChart.getContext('2d')
      const labels = this.monthOrder.map(m => this.thaiMonthShort(m))

      const datasets = this.availableRiskColors.map(color => {
        return {
          label: this.colorLabels[color] || color.toUpperCase(),
          backgroundColor: this.colorStyles[color] || '#ced8e2',
          borderColor: this.colorStyles[color] || '#ced8e2',
          borderWidth: 1,
          data: this.monthOrder.map(month => this.monthlyAggregates[month]?.[color] || 0),
          stack: 'total',
        }
      })

      this.chartInstance = new Chart(ctx, {
        type: 'bar',
        data: { labels, datasets },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { position: 'top' },
            tooltip: { enabled: true },
            title: { display: false },
          },
          scales: {
            x: { stacked: true },
            y: { stacked: true, beginAtZero: true, title: { display: true, text: 'AN (ครั้ง)' } },
          },
        },
      })
    },
  }
}
</script>

<style scoped>
.my-card-header {
  border-bottom: 0;
  padding-top: 0.1rem;
}
/* 
.card-body {
  min-height: 420px;
} */
.chart-wrapper {
  position: relative;
  width: 100%;
  height: 100%;
}
.chart-container {
  position: relative;
  width: 100%;
  height: 360px;
}
.chart-container canvas {
  width: 100% !important;
  height: 100% !important;
}

.risk-badge {
  display: inline-block;
  padding: 0.25rem 0.75rem;
  font-size: 0.9rem;
  font-weight: 500;
  color: white;
  border-radius: 0.25rem;
  width: 100%;
}

.thead-light th {
  background-color: #f8f9fa;
  border-bottom: 2px solid #dee2e6;
}

.table-footer {
  border-top: 2px solid #dee2e6;
  background-color: #f8f9fa;
}

.table td, .table th {
  padding: 0.75rem 0.5rem;
  vertical-align: middle;
}

.table tbody tr {
  height: 30px;
}

.table tbody tr:hover {
  background-color: #f5f5f5;
}
</style>
